import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from '../lib/firebase/authReadyCallable';
import { toast } from 'react-hot-toast';
import { functions } from '../config/firebase';
import { APP_VERSION } from '../config/appVersion';
import { v4 as uuidv4 } from 'uuid';
import type { ErrorLog, LogErrorRequest, ErrorSeverity } from '../types/analytics';
import { logCallableFailure } from '../lib/logCallableFailure';

// Session ID management (persists across page loads within the same session)
const SESSION_STORAGE_KEY = 'quoter_session_id';

function getOrCreateSessionId(): string {
  const sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!sessionId) {
    const newSessionId = uuidv4();
    sessionStorage.setItem(SESSION_STORAGE_KEY, newSessionId);
    return newSessionId;
  }
  return sessionId;
}

// Get browser and device info
function getBrowserInfo() {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let browserVersion = '';

  if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edg') === -1) {
    browser = 'Chrome';
    const match = ua.match(/Chrome\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
    browser = 'Safari';
    const match = ua.match(/Version\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (ua.indexOf('Firefox') > -1) {
    browser = 'Firefox';
    const match = ua.match(/Firefox\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (ua.indexOf('Edg') > -1) {
    browser = 'Edge';
    const match = ua.match(/Edg\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : '';
  }

  return { browser, browserVersion };
}

function getOSInfo() {
  const ua = navigator.userAgent;
  let os = 'Unknown';
  let osVersion = '';

  if (ua.indexOf('Win') > -1) {
    os = 'Windows';
    if (ua.indexOf('Windows NT 10.0') > -1) osVersion = '10';
    else if (ua.indexOf('Windows NT 6.3') > -1) osVersion = '8.1';
    else if (ua.indexOf('Windows NT 6.2') > -1) osVersion = '8';
    else if (ua.indexOf('Windows NT 6.1') > -1) osVersion = '7';
  } else if (ua.indexOf('Mac') > -1) {
    os = 'macOS';
    const match = ua.match(/Mac OS X (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : '';
  } else if (ua.indexOf('Linux') > -1) {
    os = 'Linux';
  } else if (ua.indexOf('Android') > -1) {
    os = 'Android';
    const match = ua.match(/Android (\d+\.\d+)/);
    osVersion = match ? match[1] : '';
  } else if (ua.indexOf('iOS') > -1 || ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) {
    os = 'iOS';
    const match = ua.match(/OS (\d+_\d+)/);
    osVersion = match ? match[1].replace('_', '.') : '';
  }

  return { os, osVersion };
}

function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Hook to log errors to Firebase
 */
export function useLogError() {
  return useMutation({
    mutationFn: async ({
      error,
      context,
      severity = 'error',
      category = 'client_error',
      is_handled = true,
    }: LogErrorRequest) => {
      const logErrorFn = httpsCallable<any, any>(functions, 'logError');

      // Extract error details
      const errorObj = error instanceof Error ? error : new Error(String(error));
      const { browser, browserVersion } = getBrowserInfo();
      const { os, osVersion } = getOSInfo();

      const errorData = {
        error_message: errorObj.message || 'Unknown error',
        error_type: errorObj.name || 'Error',
        error_code: (error as any)?.code,
        stack_trace: errorObj.stack,
        component_stack: (error as any)?.componentStack,

        severity,
        category,
        is_handled,

        action_attempted: context?.action_attempted,
        affected_feature: context?.affected_feature,
        current_route: window.location.pathname,
        additional_context: context?.additional_context,

        browser,
        browser_version: browserVersion,
        os,
        os_version: osVersion,
        device_type: getDeviceType(),
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        user_agent: navigator.userAgent,
        app_version: APP_VERSION || '1.0.0',
        environment: (process.env.NODE_ENV === 'production' ? 'production' : 'development') as 'production' | 'staging' | 'development',

        session_id: getOrCreateSessionId(),
      };

      const result = await logErrorFn(errorData);
      return result.data;
    },
    onError: (error) => {
      // Silently fail - don't want error tracking to break the app
      // eslint-disable-next-line no-restricted-syntax -- the error logger itself: routing its own failure through the demoting helper could hide it
      console.error('Failed to log error to analytics:', error);
    },
  });
}

/**
 * Convert a serialized Firestore Timestamp ({_seconds, _nanoseconds})
 * to an object with a toDate() method, matching the Firestore Timestamp API.
 */
function toTimestampLike(value: any): any {
  if (!value) return value;
  if (typeof value.toDate === 'function') return value;
  if (typeof value._seconds === 'number') {
    return { ...value, toDate: () => new Date(value._seconds * 1000) };
  }
  return value;
}

/**
 * Maps an ErrorGroup (backend) to ErrorLog (UI) shape.
 * The backend stores error_groups with different field names than the
 * legacy ErrorLog type the UI renders. This adapter bridges them.
 */
function mapGroupToErrorLog(group: any): ErrorLog {
  return {
    errorLogId: group.fingerprint || '',
    event_id: group.fingerprint || '',
    event_timestamp: toTimestampLike(group.lastSeenAt),
    user_email: group.lastUserEmail,
    session_id: '',
    severity: group.severity || 'error',
    category: group.category || 'unknown',
    error_message: group.message || 'Unknown error',
    error_type: group.name,
    error_code: group.code,
    stack_trace: group.stack,
    current_route: group.lastRoute || '',
    browser: group.browser || '',
    os: group.os || '',
    device_type: group.deviceType || 'desktop',
    environment: group.environment || 'production',
    app_version: group.appVersion || '',
    user_agent: '',
    is_handled: true,
    occurrences: group.occurrenceCount || 1,
    first_occurrence: toTimestampLike(group.firstSeenAt),
    status: group.status || 'new',
    affected_feature: group.functionName || group.componentName,
    additional_context: group.additionalContext,
    resolution_notes: group.resolution,
    createdAt: toTimestampLike(group.createdAt),
    updatedAt: toTimestampLike(group.updatedAt),
  } as ErrorLog;
}

/**
 * Hook to fetch error logs (restricted to authorized users).
 * Calls getErrorGroups backend and maps to ErrorLog shape for the UI.
 */
export function useErrorLogs(
  status: 'new' | 'acknowledged' | 'investigating' | 'resolved' | 'ignored' | 'all' = 'new',
  severity?: ErrorSeverity
) {
  return useQuery({
    queryKey: ['errorLogs', status, severity],
    queryFn: async (): Promise<ErrorLog[]> => {
      const getErrorGroupsFn = httpsCallable<any, any>(functions, 'getErrorGroups');

      // Backend uses 'closed' instead of 'ignored', and doesn't support 'all'
      const backendStatus = status === 'all' ? undefined : status === 'ignored' ? 'closed' : status;

      const result = await getErrorGroupsFn({
        status: backendStatus,
        severity,
        limit: 100,
      });

      const groups = result.data.groups || [];
      return groups.map(mapGroupToErrorLog);
    },
    retry: 1,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Hook to update error log status
 */
export function useUpdateErrorStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      errorLogId,
      status,
      resolution_notes,
    }: {
      errorLogId: string;
      status: 'new' | 'acknowledged' | 'investigating' | 'resolved' | 'ignored';
      resolution_notes?: string;
    }) => {
      // Calls the DataHub implementation (writes `resolution` + statusHistory +
      // assignedTo), matching what mapGroupToErrorLog reads and what
      // useErrorAnalytics.ts already correctly calls. The legacy `updateErrorStatus`
      // callable writes a `resolution_notes` field that no reader consumes and
      // never records statusHistory — see payload-contract-mismatch audit finding.
      const updateErrorStatusFn = httpsCallable<any, any>(functions, 'dataHub_updateErrorStatus');

      const backendStatus = status === 'ignored' ? 'closed' : status;

      const result = await updateErrorStatusFn({
        fingerprint: errorLogId,
        status: backendStatus,
        ...(resolution_notes && { resolution: resolution_notes }),
      });

      return result.data;
    },
    onMutate: async ({ errorLogId, status }) => {
      // Cancel in-flight queries to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['errorLogs'] });

      // Snapshot all errorLogs queries for rollback
      const previousQueries = queryClient.getQueriesData<ErrorLog[]>({ queryKey: ['errorLogs'] });

      // Optimistically write the SAME value the server will store: the backend
      // maps 'ignored' -> 'closed' and mapGroupToErrorLog surfaces the stored
      // value as-is, so writing 'ignored' here would flip to 'closed' after the
      // settle refetch. The ErrorLog type predates the backend rename, hence
      // the cast.
      const backendStatus = (status === 'ignored' ? 'closed' : status) as ErrorLog['status'];
      queryClient.setQueriesData<ErrorLog[]>(
        { queryKey: ['errorLogs'] },
        (old) => old?.map((log) =>
          log.errorLogId === errorLogId ? { ...log, status: backendStatus } : log
        ),
      );

      return { previousQueries };
    },
    onError: (error, _vars, context) => {
      logCallableFailure('Failed to update error status:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update error status');
      // Rollback on error
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['errorLogs'] });
    },
  });
}

/**
 * Hook to automatically log errors
 * Use in error boundaries or catch blocks
 */
export function useAutoErrorLogger() {
  const { mutate: logError } = useLogError();

  return {
    logError: (error: Error | unknown, context?: LogErrorRequest['context']) => {
      logError({
        error,
        context,
        severity: 'error',
        category: 'client_error',
        is_handled: true,
      });
    },
    logCriticalError: (error: Error | unknown, context?: LogErrorRequest['context']) => {
      logError({
        error,
        context,
        severity: 'critical',
        category: 'client_error',
        is_handled: true,
      });
    },
    logWarning: (error: Error | unknown, context?: LogErrorRequest['context']) => {
      logError({
        error,
        context,
        severity: 'warning',
        category: 'client_error',
        is_handled: true,
      });
    },
  };
}
